import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    useBreakpointValue,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useStoreLocatorStyles } from '../styles/storeLocatorStyles'

/* Map components */
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';

/* Configs */
import { STORE_LOCATOR_CONFIGS, getLocaleDefaults } from '../configs'
import { useIntl } from 'react-intl'
import { MAP_THEMES } from '../map-themes'

/* Context */
import { useStoreLocator } from '../../../contexts/store-locator-context';
import {useBrand} from '../../../contexts/brand-context'

/* Utils */
import { getStoreDirectionsURL } from '../../../utils/storelocator-utils'

const CustomMarker = ({position, id, isActive, onClick, number, store}) => {
    const {isGarage} = useBrand()
    const theme = useTheme()

    const createNumberedIcon = (isActive, number) => {
        const markerFillColor = theme.colors.webTheme.iconPrimary
        const activeMarkerFillColor = isGarage ? theme.colors.webTypo.textBrandLoyalty : theme.colors.webTheme.iconLoyaltyIcon
        const activeMarkerTextColor = isGarage ? theme.colors.webTypo.textOnColor : theme.colors.webTypo.textPrimary

        const svgIcon = `
            <svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24.96" rx="4.25978" fill="${markerFillColor}"/>
                <path d="M12.4813 28.7998L9.98718 24.4798L14.9755 24.4798L12.4813 28.7998Z" fill="${markerFillColor}"/>
                <text
                    x="12" y="18"
                    font-family="sans-serif"
                    font-weight="500"
                    font-size="15px"
                    text-anchor="middle"
                    fill="#ffffff">${number}</text>
            </svg>
        `;

        const activeSvgIcon = `
            <svg width="34" height="41" viewBox="0 0 34 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="34.0004" height="35.36" rx="6.03473" fill="${activeMarkerFillColor}"/>
                <path d="M17.6817 40.7993L14.1482 34.6793L21.2151 34.6793L17.6817 40.7993Z" fill="${activeMarkerFillColor}"/>
                <text
                    x="17" y="25"
                    font-family="sans-serif"
                    font-weight="500"
                    font-size="24px"
                    text-anchor="middle"
                    fill="${activeMarkerTextColor}">${number}</text>
            </svg>
        `;

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(isActive ? activeSvgIcon : svgIcon)}`
        };
    };

    const handleMarkerClick = () => {
        if (onClick) {
            onClick(store, id);
        }
    };

    return (
        <Marker
            position={position}
            icon={createNumberedIcon(isActive, number)}
            onClick={handleMarkerClick}
        />
    );
}

const StoreLocatorMap = ({
    openGoogleMapsOnClickMarker = false,
    alwaysFitBounds = false
}) => {
    const mapContainerRef = useRef(null)
    const parseCoordinate = (value) => {
        if (value === '' || value === null || value === undefined) {
            return null
        }

        const parsedValue = Number(value)
        return Number.isFinite(parsedValue) ? parsedValue : null
    }

    const getCoordinates = (store) => {
        if (!store) {
            return null
        }

        const lat = parseCoordinate(store?.latitude ?? store?.lat)
        const lng = parseCoordinate(store?.longitude ?? store?.lng)

        if (lat !== null && lng !== null) {
            return {lat, lng}
        }

        return null
    }

    const styles = useStoreLocatorStyles()
    const mapStyling = styles?.components?.map || {
        desktop: {
            width: '100%',
            height: '100%'
        },
        mobile: {
            width: '100vw',
            height: '515px'
        }
    }
    const isMobile = useBreakpointValue({ base: true, md: false })
    const intl = useIntl()
    const localeDefaults = getLocaleDefaults(intl.locale)

    const {
        setMap,
        stores,
        searchValue,
        noSearchResults,
        shouldUseDefaultMapViewport,
        permissionStatus,
        selectedStore,
        setSelectedStore,
        activeMarkerId,
        setActiveMarkerId,
        setToggleStoreInfo,
        onOpenStoreDetails
    } = useStoreLocator()
    const map = useMap()
    const storesWithCoordinates = useMemo(
        () => (stores || []).map((store) => ({store, coords: getCoordinates(store)})).filter(({coords}) => !!coords),
        [stores]
    )
    const selectedStoreCoordinates = useMemo(() => getCoordinates(selectedStore), [selectedStore])
    const defaultCenter = useMemo(() => ({
        lat: localeDefaults.defaultCenter.latitude,
        lng: localeDefaults.defaultCenter.longitude
    }), [localeDefaults.defaultCenter.latitude, localeDefaults.defaultCenter.longitude])
    const localeInitialZoom = localeDefaults.initialZoom ?? STORE_LOCATOR_CONFIGS.initialZoom
    const [singleWorldMinZoom, setSingleWorldMinZoom] = useState(0)

    useEffect(() => {
        const updateSingleWorldMinZoom = () => {
            const containerWidth = mapContainerRef.current?.offsetWidth

            if (!Number.isFinite(containerWidth) || containerWidth <= 0) {
                return
            }

            // Keep the minimum zoom high enough that a single world fills the
            // container width. Zooming out further would make the world narrower
            // than the viewport, causing Google Maps to repeat it horizontally
            // (infinite horizontal drag).
            const nextMinZoom = Math.max(0, Math.ceil(Math.log2(containerWidth / 256)))

            setSingleWorldMinZoom((prevMinZoom) => (
                prevMinZoom === nextMinZoom ? prevMinZoom : nextMinZoom
            ))
        }

        updateSingleWorldMinZoom()

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
                updateSingleWorldMinZoom()
            })

            if (mapContainerRef.current) {
                resizeObserver.observe(mapContainerRef.current)
            }

            return () => {
                resizeObserver.disconnect()
            }
        }

        window.addEventListener('resize', updateSingleWorldMinZoom)

        return () => {
            window.removeEventListener('resize', updateSingleWorldMinZoom)
        }
    }, [])

    const effectiveMinZoom = Math.max(singleWorldMinZoom, 0)

    const fitMapBounds = (storesForBounds) => {
        if (!map || !window.google?.maps || storesForBounds?.length === 0) return;

        // For a single result, explicitly center the map.
        // fitBounds on one coordinate can keep marker visually off-center on some mobile viewports.
        if (storesForBounds.length === 1) {
            map.panTo(storesForBounds[0].coords)
            map.setZoom(STORE_LOCATOR_CONFIGS.centerZoom)
            return
        }

        const bounds = new google.maps.LatLngBounds();
        storesForBounds?.forEach(({coords}) => {
            bounds.extend(new google.maps.LatLng(coords.lat, coords.lng));
        });

        map?.fitBounds(bounds);
    }

    useEffect(() => {
        if (map && selectedStore) {
            setMap(map)
            return
        }

        if (!alwaysFitBounds && map && (noSearchResults || shouldUseDefaultMapViewport || (permissionStatus !== 'granted' && !searchValue)) && !selectedStore) {
            map.panTo(defaultCenter)
            map.setZoom(localeInitialZoom)
            setMap(map)
            return
        }

        fitMapBounds(storesWithCoordinates)
        setMap(map)
    }, [map, permissionStatus, selectedStore, defaultCenter, setMap, storesWithCoordinates, alwaysFitBounds, searchValue, noSearchResults, shouldUseDefaultMapViewport]);

    useEffect(() => {
        // Center and zoom selected store on the map
        if (selectedStoreCoordinates && map) {
            map.panTo(selectedStoreCoordinates)
            map.setZoom(STORE_LOCATOR_CONFIGS.centerZoom)
        }
    }, [selectedStoreCoordinates, map]);


    const handleMarkerClick = (store, markerId) => {
        setActiveMarkerId(markerId)
        setSelectedStore(store)

        if (isMobile) {
            setToggleStoreInfo(true)
        }

        onOpenStoreDetails()

        const storeCoordinates = getCoordinates(store)

        if (openGoogleMapsOnClickMarker && storeCoordinates) {
            window.open(getStoreDirectionsURL(store), '_blank')
        }
    };

    // Get the selected theme
    const selectedTheme = MAP_THEMES[STORE_LOCATOR_CONFIGS.mapTheme] || MAP_THEMES.default

    return (
        <Box
            ref={mapContainerRef}
            w={{base: mapStyling.mobile.width, lg: mapStyling.desktop.width}}
            h={{base: mapStyling.mobile.height, lg: mapStyling.desktop.height}}
        >
        <Map
            style={{width: '100%', height: '100%'}}
            defaultCenter={defaultCenter}
            defaultZoom={localeInitialZoom}
            minZoom={effectiveMinZoom}
            restriction={STORE_LOCATOR_CONFIGS.mapRestriction}
            gestureHandling={STORE_LOCATOR_CONFIGS.gestureHandling}
            disableDefaultUI={STORE_LOCATOR_CONFIGS.disableDefaultUI}
            styles={selectedTheme}>
            {storesWithCoordinates?.map(({store, coords}, index) => (
                <CustomMarker
                    key={`marker-${store?.id}`}
                    id={store?.id}
                    position={coords}
                    number={index + 1}
                    isActive={activeMarkerId === store?.id}
                    onClick={handleMarkerClick}
                    store={store} />
            ))}
        </Map>        </Box>    )
}

export default StoreLocatorMap
